/* eslint-disable no-param-reassign */
import { createAsyncThunk } from '@reduxjs/toolkit';

import l10n from '../../shared/strings';
import { fetchAggregatedProductDetails } from './fetchAggregatedProductDetails';

const strings = l10n.ACTIONS_TOOLBAR.PRODUCTS;

const toId = (value) => String(value?.id ?? value);

const collectUnsuccessfulIds = (data) => new Set([
  ...(Array.isArray(data?.skipped) ? data.skipped : []),
  ...(Array.isArray(data?.failed) ? data.failed : []),
].map(toId));

const listingLevelCounts = ({
  productIds,
  masterVariantIds,
  entries = [],
  unsuccessfulIds,
  drafted,
}) => {
  const listingIds = [...new Set([
    ...productIds.map(String),
    ...entries
      .filter((entry) => entry?.type === 'listingVariant' && entry.baseProductId)
      .map((entry) => String(entry.baseProductId)),
  ])];

  if (!listingIds.length) {
    const variantTotal = masterVariantIds.length;
    const variantDrafted = masterVariantIds.filter((id) => !unsuccessfulIds.has(String(id))).length;
    return {
      drafted: variantDrafted || drafted,
      total: variantTotal || drafted,
    };
  }

  const draftedListingCount = listingIds.filter((listingId) => {
    if (productIds.map(String).includes(listingId)) {
      return !unsuccessfulIds.has(listingId);
    }
    const variantIds = entries
      .filter((entry) => (
        entry?.type === 'listingVariant'
        && String(entry.baseProductId) === listingId
        && entry.masterVariantId
      ))
      .map((entry) => String(entry.masterVariantId));
    return variantIds.some((variantId) => !unsuccessfulIds.has(variantId));
  }).length;

  return { drafted: draftedListingCount, total: listingIds.length };
};

export const editLiveAttributes = createAsyncThunk(
  'products/editLiveAttributes',
  async ({
    productIds = [],
    masterVariantIds = [],
    channelId,
    entries = [],
  }, {
    dispatch,
    extra: {
      sdk, Toast, Logger,
    },
  }) => {
    try {
      const payload = {};
      if (productIds.length) payload.productIds = productIds;
      if (masterVariantIds.length) {
        payload.masterVariantIds = masterVariantIds;
        payload.salesChannelIds = [channelId];
      }

      const { data } = await sdk.products.createListingDrafts(payload);
      const drafted = Number(data?.drafted) || 0;

      if (!drafted) {
        Toast(strings.draftsPreparedNone, { type: 'error', autoClose: false });
        return { ...data, drafted: 0, didTransition: false };
      }

      const unsuccessfulIds = collectUnsuccessfulIds(data);
      const counts = listingLevelCounts({
        productIds,
        masterVariantIds,
        entries,
        unsuccessfulIds,
        drafted,
      });
      const remaining = Math.max(0, counts.total - counts.drafted);

      if (remaining) {
        Toast(
          `${strings.draftsPreparedSuccess(counts.drafted, counts.total)} ${strings.draftsPreparedForEditingPartial(remaining)}`,
          { type: 'warning', autoClose: false },
        );
      } else {
        Toast(strings.draftsPreparedSuccess(counts.drafted, counts.total || counts.drafted), {
          type: 'success',
        });
      }

      await dispatch(fetchAggregatedProductDetails());

      return { ...data, drafted, didTransition: false };
    } catch (error) {
      Toast(strings.draftsPreparedNone, { type: 'error', autoClose: false });
      Logger.error(new Error('Bulk edit live attributes failed'), { error }, [
        'EditLiveAttributesFailed',
        { errorMessage: error?.message || 'NoMessage' },
      ]);
      throw error;
    }
  },
);

export const reducer = (builder) => {
  builder
    .addCase(editLiveAttributes.rejected, (state, { error }) => {
      state.error = error?.message || 'Cannot prepare drafts from published records';
    });
};
